import { cn } from "@lib/cn"
import type { ReactNode } from "react"

/**
 * An empty state teaches the surface (PRD-60 chunk 0); it does not report
 * absence. The shape: what this surface is for, the one action that fills
 * it, and — when the surface is a destination rather than a list — what will
 * appear here once it does.
 */
export function EmptyState({
  title,
  message,
  action,
  secondaryAction,
  hint,
  icon,
  inline = false,
  headingLevel = 2,
  className,
}: {
  title: string
  message?: string
  /** The single move that resolves the emptiness. */
  action?: ReactNode
  secondaryAction?: ReactNode
  /** What lands here once the action is taken. Keep to one short line each. */
  hint?: ReactNode
  icon?: ReactNode
  /** Inside a card that already frames it (a section of a settings page):
   *  no dashed card of its own, since cards do not nest. */
  inline?: boolean
  /** 3 when the surrounding section already has the h2. */
  headingLevel?: 2 | 3
  className?: string
}) {
  const Heading = headingLevel === 3 ? "h3" : "h2"
  return (
    <div
      className={cn(
        "text-center",
        inline ? "py-6" : "rounded-card border-border bg-surface border border-dashed px-6 py-10",
        className,
      )}
    >
      {icon && (
        <div
          aria-hidden
          className="bg-accent-soft text-accent-ink mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full"
        >
          {icon}
        </div>
      )}
      <Heading className="text-ink text-base font-semibold">{title}</Heading>
      {message && <p className="text-ink-muted mx-auto mt-1 max-w-prose text-sm">{message}</p>}
      {(action || secondaryAction) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
      {hint && <div className="text-meta mx-auto mt-4 max-w-prose text-left">{hint}</div>}
    </div>
  )
}
